// Metadata extraction utility for Cloudinary uploads
import { CloudinaryImage } from '../types/Gallery';

export interface CloudinaryUploadResult {
  public_id: string;
  secure_url: string;
  width: number;
  height: number;
  bytes: number;
  format: string;
  created_at: string;
  folder: string;
  filename?: string;
  tags?: string[];
  // EXIF data (if available)
  exif?: {
    Make?: string;
    Model?: string;
    LensModel?: string;
    ISO?: number;
    FNumber?: string;
    ExposureTime?: string;
    FocalLength?: string;
    Flash?: number;
    WhiteBalance?: number;
    DateTimeOriginal?: string;
    GPSLatitude?: number;
    GPSLongitude?: number;
    GPSAltitude?: number;
  };
  // Context metadata
  context?: {
    caption?: string;
    alt?: string;
    copyright?: string;
    photographer?: string;
    location?: string;
    event?: string;
    client?: string;
    [key: string]: any;
  };
  // Image analysis results
  image_metadata?: {
    colors?: Array<{ color: string; percent: number }>;
    faces?: number;
    objects?: Array<{ object: string; confidence: number }>;
  };
}

/**
 * Extract and normalize metadata from Cloudinary upload result
 */
export const extractMetadata = (result: CloudinaryUploadResult): CloudinaryImage => {
  const baseImage: CloudinaryImage = {
    publicId: result.public_id,
    public_id: result.public_id,
    secure_url: result.secure_url,
    url: result.secure_url,
    width: result.width,
    height: result.height,
    bytes: result.bytes,
    folder: result.folder,
    filename: result.filename,
    format: result.format,
    created_at: result.created_at,
    uploaded_at: new Date().toISOString(),
    tags: result.tags || [],
  };

  // Extract EXIF data
  if (result.exif) {
    baseImage.exif = {
      make: result.exif.Make,
      model: result.exif.Model,
      lens: result.exif.LensModel,
      iso: result.exif.ISO,
      aperture: result.exif.FNumber ? `f/${parseFloat(result.exif.FNumber).toFixed(1)}` : undefined,
      shutter_speed: result.exif.ExposureTime ? `${result.exif.ExposureTime}s` : undefined,
      focal_length: result.exif.FocalLength ? `${result.exif.FocalLength}mm` : undefined,
      flash: result.exif.Flash === 1,
      white_balance: result.exif.WhiteBalance === 1 ? 'Auto' : 'Manual',
      date_taken: result.exif.DateTimeOriginal,
      gps: result.exif.GPSLatitude && result.exif.GPSLongitude ? {
        latitude: result.exif.GPSLatitude,
        longitude: result.exif.GPSLongitude,
        altitude: result.exif.GPSAltitude,
      } : undefined,
    };
  }

  // Extract context metadata
  if (result.context) {
    baseImage.context = {
      caption: result.context.caption || '',
      alt: result.context.alt || '',
      copyright: result.context.copyright || '© Carmen Lorraine Photography',
      photographer: result.context.photographer || 'Carmen Lorraine',
      location: result.context.location || '',
      event: result.context.event || '',
      client: result.context.client || '',
    };
  }

  // Set alt text from context or generate from filename
  if (result.context?.alt) {
    baseImage.alt = result.context.alt;
  } else if (result.filename) {
    baseImage.alt = result.filename.replace(/[_-]/g, ' ').replace(/\.[^/.]+$/, '');
  }

  return baseImage;
};

/**
 * Generate a human-readable camera settings string
 */
export const formatCameraSettings = (exif: CloudinaryImage['exif']): string => {
  if (!exif) return '';
  
  const settings = [];
  if (exif.focal_length) settings.push(exif.focal_length);
  if (exif.aperture) settings.push(exif.aperture);
  if (exif.shutter_speed) settings.push(exif.shutter_speed);
  if (exif.iso) settings.push(`ISO ${exif.iso}`);
  
  return settings.join(' • ');
};

/**
 * Generate a camera info string
 */
export const formatCameraInfo = (exif: CloudinaryImage['exif']): string => {
  if (!exif) return '';
  
  const info = [];
  if (exif.make && exif.model) {
    info.push(`${exif.make} ${exif.model}`);
  }
  if (exif.lens) {
    info.push(exif.lens);
  }
  
  return info.join(' + ');
};

/**
 * Format GPS location for display
 */
export const formatLocation = (gps: NonNullable<CloudinaryImage['exif']>['gps']): string => {
  if (!gps || !gps.latitude || !gps.longitude) return '';
  
  const lat = gps.latitude >= 0 ? `${gps.latitude.toFixed(6)}°N` : `${Math.abs(gps.latitude).toFixed(6)}°S`;
  const lng = gps.longitude >= 0 ? `${gps.longitude.toFixed(6)}°E` : `${Math.abs(gps.longitude).toFixed(6)}°W`;
  
  return `${lat}, ${lng}`;
};

/**
 * Generate automatic tags based on metadata
 */
export const generateAutoTags = (image: CloudinaryImage): string[] => {
  const tags = [];
  
  // Add camera make/model tags
  if (image.exif?.make) {
    tags.push(image.exif.make.toLowerCase());
  }
  if (image.exif?.model) {
    tags.push(image.exif.model.toLowerCase().replace(/\s+/g, '-'));
  }
  
  // Add lens tags
  if (image.exif?.lens) {
    const lensBrand = image.exif.lens.split(' ')[0].toLowerCase();
    tags.push(lensBrand);
  }
  
  // Add location tags if GPS available
  if (image.exif?.gps?.latitude && image.exif?.gps?.longitude) {
    tags.push('gps-located');
  }
  
  // Add format tag
  if (image.format) {
    tags.push(image.format.toLowerCase());
  }
  
  // Add size category
  if (image.width && image.height) {
    const megapixels = (image.width * image.height) / 1000000;
    if (megapixels > 20) tags.push('high-resolution');
    else if (megapixels > 10) tags.push('medium-resolution');
    else tags.push('standard-resolution');
  }
  
  return tags.filter(tag => tag && tag.length > 0);
};
